import type { AgentProposal, LineItemChangeInput } from "@rebuild/shared/models";
import { AgentProposalChangePath, AgentProposalToolName } from "@rebuild/shared/models";
import type { EstimateDetail } from "@/interfaces/estimate-detail";
import { applyUpdateToState } from "@/hooks/useEstimateDetail.helpers";
import { applyDeleteToState } from "@/lib/estimate-line-items";

interface LineItemLocation {
  pageId: string;
  tupleId: string;
  lineItemId: string;
  uniqueId: string;
  isLikely: boolean;
}

function findLineItemLocation(state: EstimateDetail[], uniqueId: string): LineItemLocation | undefined {
  for (const room of state) {
    for (const tuple of room.dataForUI?.page?.tuplesOutput ?? []) {
      const lineItem = tuple.lineItems.find((li) => li.uniqueId === uniqueId);
      if (lineItem) {
        return { pageId: room.dataForUI.metadata.pageId, tupleId: tuple.tupleId, lineItemId: lineItem.lineItemId, uniqueId, isLikely: !!tuple.isLikely };
      }
    }
  }
  return undefined;
}

const proposedValue = (input: LineItemChangeInput, path: AgentProposalChangePath) =>
  input.changes.find((change) => change.path === path)?.newValue;

// Same shape Line.tsx persists for a typed formula; the server computes the quantity.
const userFormula = (rawFormula: string) => ({
  initialValue: 1,
  base: "ea",
  operator: "",
  modifierValue: 0,
  modifierSuffix: "",
  rawFormula,
  isMultiUnit: true,
  source: "user" as const,
});

// A created room has no page loaded yet; the refresh after accept brings it in.
function applyRoomChange(state: EstimateDetail[], proposal: AgentProposal): EstimateDetail[] {
  const input = proposal.toolCallInput;
  if (input.changeType !== "room") return state;
  switch (proposal.toolName) {
    case AgentProposalToolName.propose_room_rename: {
      const { roomId, newName } = input;
      if (!newName) return state;
      return state.map((room) => {
        if (room.dataForUI.metadata.pageId !== roomId) return room;
        return {
          ...room,
          dataForUI: { ...room.dataForUI, roomName: newName, metadata: { ...room.dataForUI.metadata, roomName: newName } },
        };
      });
    }
    // The loaded estimate leaves archived rooms out, so an archived room is dropped.
    case AgentProposalToolName.propose_room_archive:
      return state.filter((room) => room.dataForUI.metadata.pageId !== input.roomId);
    default:
      return state;
  }
}

/**
 * Patches accepted agent proposals into the loaded estimate using the same state updaters as manual
 * edits, so the room view reflects them right away. Not an exact mirror of the server (e.g. a
 * formula's recomputed quantity); the background refresh after accept corrects any difference,
 * and is also what brings in a created room. Returns the same array when nothing matched, except
 * that a room rename always returns a new one.
 */
export function applyAcceptedProposalsToEstimationDetail(state: EstimateDetail[], proposals: AgentProposal[]): EstimateDetail[] {
  return proposals.reduce((current, proposal) => {
    const input = proposal.toolCallInput;
    if (input.changeType === "room") return applyRoomChange(current, proposal);
    // Not patched yet: new line items arrive with the background refresh.
    if (input.changeType === "lineItemAdd") return current;
    const location = findLineItemLocation(current, input.lineItemUniqueId);
    if (!location) return current;

    switch (proposal.toolName) {

      case AgentProposalToolName.propose_line_item_archive:
        //  archiving an item in a recommendation (isLikely) tuple archives the whole tuple.
        return applyDeleteToState(current, { ...location, category: location.isLikely ? "recommended" : undefined }).modifiedState ?? current;

      case AgentProposalToolName.propose_line_item_formula_update: {
        const newFormula = proposedValue(input, AgentProposalChangePath.lineItemformularawFormula);
        if (typeof newFormula !== "string") return current;
        return applyUpdateToState(current, { ...location, formula: userFormula(newFormula), confirmed: true }).modifiedState ?? current;
      }

      // The new name is the new set of options.
      case AgentProposalToolName.propose_line_item_permutation_update: {
        const newName = proposedValue(input, AgentProposalChangePath.lineItemlineItem);
        if (typeof newName !== "string") return current;
        return applyUpdateToState(current, { ...location, item: newName, confirmed: true }).modifiedState ?? current;
      }
      default:
        return current;
    }
  }, state);
}
